import React, { useContext, useEffect, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Input } from '../../../../catalyst-components/input';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { AssistantDialog } from './assistant-dialog';
import { Assistant, SkillAssistant } from '../../../../shared/models';
import { SkillAssistantTable } from './components/skill-assistant-table';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';

export const SkillDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [completionXpBonus, setCompletionXpBonus] = useState(100);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [skillId, setSkillId] = useState<Id<"skills"> | null>(null);
  const { convexUserId } = useContext(BaseContext);
  const [showModal, setShowModal] = useState(false);
  const [confirmDeleteAssistant, setConfirmDeleteAssistant] = useState<{ open: boolean, assistantId: Id<"skill_assistants">, assistantName: string } | null>(null);
  
  // Get assistants list for dropdown
  const assistants = (useQuery(api.assistants.listActive) || []) as Assistant[];
  
  // Get mutations
  const createSkillAssistant = useMutation(api.skill_assistants.create);
  const createSkill = useMutation(api.skills.create);
  const updateSkill = useMutation(api.skills.update);

  useEffect(() => {
    if (id && id !== 'new') {
      setSkillId(id as Id<"skills">);
    }
  }, [id]);

  // Get existing skill data if editing
  const skill = useQuery(
    api.skills.getById, 
    skillId ? { id: skillId as Id<"skills"> } : 'skip'
  );

  // Load existing skill data
  useEffect(() => {
    if (skill) {
      setName(skill.name);
      setDescription(skill.description || '');
      setShortDescription(skill.short_description || '');
      setCompletionXpBonus(skill.completion_xp_bonus ?? 100);
    }
  }, [skill]);

  // Add query for skill assistants
  const skillAssistants = useQuery(
    api.skill_assistants.listBySkillId, 
    skillId ? { skill_id: skillId } : 'skip'
  );

  const handleSubmitSkill = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (skillId) {
        // Update existing skill
        await updateSkill({
          id: skillId as Id<"skills">,
          name,
          description,
          short_description: shortDescription,
          completion_xp_bonus: completionXpBonus,
          modified_by: convexUserId as Id<"users">
        });
        setAlertMessage('Skill updated successfully!');
      } else {
        // Create new skill
        await createSkill({
          name,
          description,
          completion_xp_bonus: completionXpBonus,
          modified_by: convexUserId as Id<"users">
        });
        setAlertMessage('Skill created successfully!');
      }
      setShowAlert(true);
      setSuccess(true);
      navigate('../');
    } catch (error) {
      setAlertMessage('Error saving skill. Please try again.');
      setShowAlert(true);
      setSuccess(false);
    }
  };

  const handleCreateSkillAssistant = async (data: {
    assistantId: Id<"assistants">;
  }) => {
    if (!skillId) return;

    try {
      await createSkillAssistant({
        skill_id: skillId,
        assistant_id: data.assistantId
      });
      
      setShowModal(false);
      setAlertMessage('Skill assistant added successfully!');
      setShowAlert(true);
      setSuccess(true);
    } catch (error) {
      setAlertMessage('Error creating skill assistant. Please try again.');
      setShowAlert(true);
      setSuccess(false);
    }
  };

  return (
    <section className="min-h-screen bg-background pt-24 px-6 flex flex-col">
      <Button 
        color="light" 
        className="mt-2 mb-2 w-80" 
        onClick={() => navigate('../')}
      >
        Go back
      </Button>

      <details className="w-1/2">
        <summary className="cursor-pointer mb-4">Skill Details</summary>
        <form onSubmit={handleSubmitSkill} className="w-full">
          <Field>
            <Label>Skill Name</Label>
            <Input
              required
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field className="mt-4">
            <Label>Description</Label>
            <Textarea
              required
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>

          <Field className="mt-4">
            <Label>Short Description</Label>
            <Textarea
              required
              name="short_description"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </Field>

          <Field className="mt-4">
            <Label>Completion Points Bonus</Label>
            <p className="text-sm text-gray-500 mb-2">
              Bonus points awarded when a user completes all assistants in this skill
            </p>
            <Input
              type="number"
              min="0"
              name="completion_xp_bonus"
              value={completionXpBonus}
              onChange={(e) => setCompletionXpBonus(parseInt(e.target.value) || 0)}
            />
          </Field>

          <Button
            color="light"
            className="mt-6 mb-8"
            type="submit"
          >
            {skillId ? 'Update Skill' : 'Add Skill'}
          </Button>
        </form>
      </details>

      {skillId && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Skill Assistants</h2>
            <Button 
              color="light" 
              onClick={() => setShowModal(true)}
            >
              Add New Assistant
            </Button>
          </div>

          <SkillAssistantTable 
            skill_id={skillId}
            assistants={skillAssistants as SkillAssistant[]}
          />
        </div>
      )}

      <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
        <AlertTitle>Notification</AlertTitle>
        <AlertDescription>{alertMessage}</AlertDescription>
        <AlertActions>
          <Button 
            plain 
            onClick={() => {
              setShowAlert(false);
              if (success) {
                navigate('../');
              }
            }}
          >
            Ok
          </Button>
        </AlertActions>
      </Alert>

      <AssistantDialog
        open={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleCreateSkillAssistant}
        assistants={assistants}
      />
    </section>
  );
};